// MUS-558 - the scale library catalog: the ruled organization of the scale
// domain (families, within-family order, the Foundations shelf, and the
// circle-of-fifths tonic display order). This is the durable structural
// layer the browse surface renders; per-scale played content (interval
// formulas, packages) is a separate content-generation concern and is not
// carried here. Data-only: 15 families, 87 listing slots, 86 distinct units
// (one scale is deliberately dual-listed and resolves to a single unit).

import type { Tonic } from 'Types/ExerciseContentTypes';

export type ScaleSalience = 'foundations' | 'catalog';

export interface ScaleFamily {
  key: string;
  name: string;
}

export interface ScaleUnit {
  /** Stable id; a dual-listed scale shares one id across its families. */
  id: string;
  name: string;
  salience: ScaleSalience;
}

export interface ScaleFamilyListing {
  family: string;
  /** Unit ids in within-family (parent-first) order. */
  unitIds: string[];
}

/** The 15 families in their catalog (suggested, non-binding) order. */
export const SCALE_FAMILIES: ScaleFamily[] = [
  { key: 'diatonic', name: 'Diatonic' },
  { key: 'melodic-minor', name: 'Melodic minor' },
  { key: 'harmonic-minor', name: 'Harmonic minor' },
  { key: 'harmonic-major', name: 'Harmonic major' },
  { key: 'diminished-octatonic', name: 'Diminished / octatonic' },
  { key: 'pentatonic', name: 'Pentatonic' },
  { key: 'hexatonic', name: 'Hexatonic' },
  { key: 'blues', name: 'Blues' },
  { key: 'bebop', name: 'Bebop' },
  { key: 'double-harmonic-major', name: 'Double harmonic major' },
  { key: 'neapolitan-major', name: 'Neapolitan major' },
  { key: 'neapolitan-minor', name: 'Neapolitan minor' },
  { key: 'hungarian-major', name: 'Hungarian major' },
  { key: 'romanian-major', name: 'Romanian major' },
  { key: 'chromatic', name: 'Chromatic' },
];

/** Every distinct scale unit, keyed by id. */
export const SCALE_UNITS: Record<string, ScaleUnit> = {
  ionian: { id: 'ionian', name: 'Ionian / Major', salience: 'foundations' },
  dorian: { id: 'dorian', name: 'Dorian', salience: 'catalog' },
  phrygian: { id: 'phrygian', name: 'Phrygian', salience: 'catalog' },
  lydian: { id: 'lydian', name: 'Lydian', salience: 'catalog' },
  mixolydian: { id: 'mixolydian', name: 'Mixolydian', salience: 'catalog' },
  aeolian: { id: 'aeolian', name: 'Aeolian / Minor', salience: 'foundations' },
  locrian: { id: 'locrian', name: 'Locrian', salience: 'catalog' },
  'ascending-melodic-minor': {
    id: 'ascending-melodic-minor',
    name: 'Ascending Melodic Minor',
    salience: 'foundations',
  },
  'phrygian-nat6': {
    id: 'phrygian-nat6',
    name: 'Phrygian \u266e6 / Dorian \u266d2 / Assyrian / Phrygidorian / Cappadocian',
    salience: 'catalog',
  },
  'lydian-augmented': {
    id: 'lydian-augmented',
    name: 'Lydian Augmented / Lydian \u266f5',
    salience: 'catalog',
  },
  'lydian-dominant': {
    id: 'lydian-dominant',
    name: 'Lydian Dominant / Lydian \u266d7 / Acoustic / Mixolydian \u266f4 / Overtone / Lydomyxian',
    salience: 'catalog',
  },
  'aeolian-dominant': {
    id: 'aeolian-dominant',
    name: 'Aeolian Dominant / Mixolydian \u266d6 / Melodic Major / Hindu / Myxaeolian',
    salience: 'catalog',
  },
  'locrian-nat2': {
    id: 'locrian-nat2',
    name: 'Locrian \u266e2 / Half-diminished / Aeolocrian',
    salience: 'catalog',
  },
  superlocrian: {
    id: 'superlocrian',
    name: 'Superlocrian / Altered / Altered Dominant / Diminished Whole-Tone',
    salience: 'catalog',
  },
  'harmonic-minor': { id: 'harmonic-minor', name: 'Harmonic Minor', salience: 'foundations' },
  'locrian-nat6': { id: 'locrian-nat6', name: 'Locrian \u266e6', salience: 'catalog' },
  'ionian-augmented': {
    id: 'ionian-augmented',
    name: 'Ionian Augmented / Major \u266f5',
    salience: 'catalog',
  },
  romanian: { id: 'romanian', name: 'Romanian / Dorian \u266f4', salience: 'catalog' },
  'phrygian-dominant': { id: 'phrygian-dominant', name: 'Phrygian Dominant', salience: 'catalog' },
  'lydian-2': { id: 'lydian-2', name: 'Lydian \u266f2', salience: 'catalog' },
  ultralocrian: { id: 'ultralocrian', name: 'Ultralocrian', salience: 'catalog' },
  'harmonic-major': { id: 'harmonic-major', name: 'Harmonic Major', salience: 'catalog' },
  'dorian-b5': { id: 'dorian-b5', name: 'Dorian \u266d5 / Heptatonic Blues', salience: 'catalog' },
  'phrygian-b4': { id: 'phrygian-b4', name: 'Phrygian \u266d4', salience: 'catalog' },
  'lydian-b3': { id: 'lydian-b3', name: 'Lydian \u266d3 / Lydian Diminished', salience: 'catalog' },
  'mixolydian-b2': { id: 'mixolydian-b2', name: 'Mixolydian \u266d2', salience: 'catalog' },
  'lydian-augmented-2': {
    id: 'lydian-augmented-2',
    name: 'Lydian Augmented \u266f2',
    salience: 'catalog',
  },
  'locrian-bb7': { id: 'locrian-bb7', name: 'Locrian \u266d\u266d7', salience: 'catalog' },
  'fully-diminished': {
    id: 'fully-diminished',
    name: 'Fully Diminished / Whole-Half Diminished',
    salience: 'catalog',
  },
  'dominant-diminished': {
    id: 'dominant-diminished',
    name: 'Dominant Diminished / Half-Whole Diminished',
    salience: 'catalog',
  },
  'minor-pentatonic': { id: 'minor-pentatonic', name: 'Minor Pentatonic', salience: 'catalog' },
  'major-pentatonic': { id: 'major-pentatonic', name: 'Major Pentatonic', salience: 'catalog' },
  'egyptian-suspended': {
    id: 'egyptian-suspended',
    name: 'Egyptian Suspended',
    salience: 'catalog',
  },
  'blues-minor-pentatonic': {
    id: 'blues-minor-pentatonic',
    name: 'Blues Minor Pentatonic / Man Gong',
    salience: 'catalog',
  },
  'blues-major-pentatonic': {
    id: 'blues-major-pentatonic',
    name: 'Blues Major Pentatonic / Ritsusen',
    salience: 'catalog',
  },
  'whole-tone': { id: 'whole-tone', name: 'Whole-Tone', salience: 'catalog' },
  augmented: { id: 'augmented', name: 'Augmented', salience: 'catalog' },
  'inverted-augmented': {
    id: 'inverted-augmented',
    name: 'Inverted Augmented',
    salience: 'catalog',
  },
  prometheus: { id: 'prometheus', name: 'Prometheus', salience: 'catalog' },
  tritone: { id: 'tritone', name: 'Tritone / Petrushka', salience: 'catalog' },
  hexatonic: { id: 'hexatonic', name: 'Hexatonic / Minor Blues', salience: 'catalog' },
  'major-blues': { id: 'major-blues', name: 'Major Blues', salience: 'catalog' },
  'nonatonic-blues': { id: 'nonatonic-blues', name: 'Nonatonic Blues', salience: 'catalog' },
  'decatonic-blues': {
    id: 'decatonic-blues',
    name: '\u201cDecatonic\u201d Blues / \u201cMinor Pentatonic with Leading Tones\u201d',
    salience: 'catalog',
  },
  'bebop-dominant': { id: 'bebop-dominant', name: 'Bebop Dominant', salience: 'catalog' },
  'bebop-dorian': { id: 'bebop-dorian', name: 'Bebop Dorian', salience: 'catalog' },
  'alternate-bebop-dorian': {
    id: 'alternate-bebop-dorian',
    name: 'Alternate Bebop Dorian',
    salience: 'catalog',
  },
  'bebop-major': {
    id: 'bebop-major',
    name: 'Bebop Major / 6th Diminished Major',
    salience: 'catalog',
  },
  'bebop-melodic-minor': {
    id: 'bebop-melodic-minor',
    name: 'Bebop Melodic Minor / 6th Diminished Minor',
    salience: 'catalog',
  },
  'bebop-harmonic-minor': {
    id: 'bebop-harmonic-minor',
    name: 'Bebop Harmonic Minor / Bebop Natural Minor',
    salience: 'catalog',
  },
  'double-harmonic-major': {
    id: 'double-harmonic-major',
    name: 'Double Harmonic Major / Arabic / Gypsy Major',
    salience: 'catalog',
  },
  'lydian-2-6': { id: 'lydian-2-6', name: 'Lydian \u266f2 \u266f6', salience: 'catalog' },
  ultraphrygian: { id: 'ultraphrygian', name: 'Ultraphrygian', salience: 'catalog' },
  'hungarian-minor': {
    id: 'hungarian-minor',
    name: 'Hungarian Minor / Gypsy Minor / Double Harmonic Minor',
    salience: 'catalog',
  },
  oriental: { id: 'oriental', name: 'Oriental', salience: 'catalog' },
  'ionian-2-5': { id: 'ionian-2-5', name: 'Ionian \u266f2 \u266f5', salience: 'catalog' },
  'locrian-bb3-bb7': {
    id: 'locrian-bb3-bb7',
    name: 'Locrian \u266d\u266d3 \u266d\u266d7',
    salience: 'catalog',
  },
  'neapolitan-major': { id: 'neapolitan-major', name: 'Neapolitan Major', salience: 'catalog' },
  'leading-whole-tone': {
    id: 'leading-whole-tone',
    name: 'Leading Whole-Tone',
    salience: 'catalog',
  },
  'lydian-dominant-augmented': {
    id: 'lydian-dominant-augmented',
    name: 'Lydian Dominant Augmented',
    salience: 'catalog',
  },
  'lydian-minor': { id: 'lydian-minor', name: 'Lydian Minor', salience: 'catalog' },
  'major-locrian': { id: 'major-locrian', name: 'Major Locrian / Arabian', salience: 'catalog' },
  'altered-dominant-major-2nd': {
    id: 'altered-dominant-major-2nd',
    name: 'Altered Dominant Major 2nd',
    salience: 'catalog',
  },
  'altered-dominant-diminished-3rd': {
    id: 'altered-dominant-diminished-3rd',
    name: 'Altered Dominant Diminished 3rd',
    salience: 'catalog',
  },
  'neapolitan-minor': { id: 'neapolitan-minor', name: 'Neapolitan Minor', salience: 'catalog' },
  'lydian-6': { id: 'lydian-6', name: 'Lydian \u266f6', salience: 'catalog' },
  'mixolydian-augmented': {
    id: 'mixolydian-augmented',
    name: 'Mixolydian Augmented',
    salience: 'catalog',
  },
  'hungarian-gypsy': {
    id: 'hungarian-gypsy',
    name: 'Hungarian Gypsy / Aeolian \u266f4',
    salience: 'catalog',
  },
  'locrian-dominant': { id: 'locrian-dominant', name: 'Locrian Dominant', salience: 'catalog' },
  'ionian-2': { id: 'ionian-2', name: 'Ionian \u266f2', salience: 'catalog' },
  'ultralocrian-bb3': {
    id: 'ultralocrian-bb3',
    name: 'Ultralocrian \u266d\u266d3',
    salience: 'catalog',
  },
  'hungarian-major': { id: 'hungarian-major', name: 'Hungarian Major', salience: 'catalog' },
  'ultralocrian-bb6': {
    id: 'ultralocrian-bb6',
    name: 'Ultralocrian \u266d\u266d6',
    salience: 'catalog',
  },
  'harmonic-minor-b5': {
    id: 'harmonic-minor-b5',
    name: 'Harmonic Minor \u266d5',
    salience: 'catalog',
  },
  'superlocrian-nat6': {
    id: 'superlocrian-nat6',
    name: 'Superlocrian \u266e6',
    salience: 'catalog',
  },
  'jazz-minor-5': { id: 'jazz-minor-5', name: 'Jazz Minor \u266f5', salience: 'catalog' },
  'dorian-b2-4': { id: 'dorian-b2-4', name: 'Dorian \u266d2 \u266f4', salience: 'catalog' },
  'nohkan-flute-scale': {
    id: 'nohkan-flute-scale',
    name: 'Nohkan Flute Scale',
    salience: 'catalog',
  },
  'romanian-major': { id: 'romanian-major', name: 'Romanian Major', salience: 'catalog' },
  'super-lydian-augmentednat6': {
    id: 'super-lydian-augmentednat6',
    name: 'Super Lydian Augmented\u266e6',
    salience: 'catalog',
  },
  'locriannat2-bb7': {
    id: 'locriannat2-bb7',
    name: 'Locrian\u266e2 \u266d\u266d7',
    salience: 'catalog',
  },
  'superlocrian-bb6': {
    id: 'superlocrian-bb6',
    name: 'Superlocrian \u266d\u266d6',
    salience: 'catalog',
  },
  'jazz-minor-b5': {
    id: 'jazz-minor-b5',
    name: 'Jazz Minor \u266d5 / Jeth\u2019s Mode',
    salience: 'catalog',
  },
  'javanese-b4': { id: 'javanese-b4', name: 'Javanese \u266d4', salience: 'catalog' },
  'lydian-augmented-b3': {
    id: 'lydian-augmented-b3',
    name: 'Lydian Augmented \u266d3',
    salience: 'catalog',
  },
  chromatic: { id: 'chromatic', name: 'Chromatic', salience: 'catalog' },
};

/** Family -> its unit ids in order. A dual-listed unit appears in two
 *  families; both point at the one unit (one progress record). */
export const SCALE_FAMILY_LISTINGS: ScaleFamilyListing[] = [
  {
    family: 'diatonic',
    unitIds: ['ionian', 'dorian', 'phrygian', 'lydian', 'mixolydian', 'aeolian', 'locrian'],
  },
  {
    family: 'melodic-minor',
    unitIds: [
      'ascending-melodic-minor',
      'phrygian-nat6',
      'lydian-augmented',
      'lydian-dominant',
      'aeolian-dominant',
      'locrian-nat2',
      'superlocrian',
    ],
  },
  {
    family: 'harmonic-minor',
    unitIds: [
      'harmonic-minor',
      'locrian-nat6',
      'ionian-augmented',
      'romanian',
      'phrygian-dominant',
      'lydian-2',
      'ultralocrian',
    ],
  },
  {
    family: 'harmonic-major',
    unitIds: [
      'harmonic-major',
      'dorian-b5',
      'phrygian-b4',
      'lydian-b3',
      'mixolydian-b2',
      'lydian-augmented-2',
      'locrian-bb7',
    ],
  },
  { family: 'diminished-octatonic', unitIds: ['fully-diminished', 'dominant-diminished'] },
  {
    family: 'pentatonic',
    unitIds: [
      'minor-pentatonic',
      'major-pentatonic',
      'egyptian-suspended',
      'blues-minor-pentatonic',
      'blues-major-pentatonic',
    ],
  },
  {
    family: 'hexatonic',
    unitIds: ['whole-tone', 'augmented', 'inverted-augmented', 'prometheus', 'tritone'],
  },
  {
    family: 'blues',
    unitIds: ['hexatonic', 'major-blues', 'dorian-b5', 'nonatonic-blues', 'decatonic-blues'],
  },
  {
    family: 'bebop',
    unitIds: [
      'bebop-dominant',
      'bebop-dorian',
      'alternate-bebop-dorian',
      'bebop-major',
      'bebop-melodic-minor',
      'bebop-harmonic-minor',
    ],
  },
  {
    family: 'double-harmonic-major',
    unitIds: [
      'double-harmonic-major',
      'lydian-2-6',
      'ultraphrygian',
      'hungarian-minor',
      'oriental',
      'ionian-2-5',
      'locrian-bb3-bb7',
    ],
  },
  {
    family: 'neapolitan-major',
    unitIds: [
      'neapolitan-major',
      'leading-whole-tone',
      'lydian-dominant-augmented',
      'lydian-minor',
      'major-locrian',
      'altered-dominant-major-2nd',
      'altered-dominant-diminished-3rd',
    ],
  },
  {
    family: 'neapolitan-minor',
    unitIds: [
      'neapolitan-minor',
      'lydian-6',
      'mixolydian-augmented',
      'hungarian-gypsy',
      'locrian-dominant',
      'ionian-2',
      'ultralocrian-bb3',
    ],
  },
  {
    family: 'hungarian-major',
    unitIds: [
      'hungarian-major',
      'ultralocrian-bb6',
      'harmonic-minor-b5',
      'superlocrian-nat6',
      'jazz-minor-5',
      'dorian-b2-4',
      'nohkan-flute-scale',
    ],
  },
  {
    family: 'romanian-major',
    unitIds: [
      'romanian-major',
      'super-lydian-augmentednat6',
      'locriannat2-bb7',
      'superlocrian-bb6',
      'jazz-minor-b5',
      'javanese-b4',
      'lydian-augmented-b3',
    ],
  },
  { family: 'chromatic', unitIds: ['chromatic'] },
];

export interface FoundationsShelfUnit {
  /** The unit id, shared with its home-family listing (one progress record). */
  id: string;
  /** The shelf's own frequency-name label for the unit. */
  name: string;
}

/** The Foundations shelf: four high-frequency units surfaced above the family
 *  catalog, in frequency order. Each also lives in its home family; the shelf
 *  label is the plain frequency name, the unit keeps its catalog name. */
export const FOUNDATIONS_SHELF: FoundationsShelfUnit[] = [
  { id: 'ionian', name: 'Major' },
  { id: 'aeolian', name: 'Natural minor' },
  { id: 'harmonic-minor', name: 'Harmonic minor' },
  { id: 'ascending-melodic-minor', name: 'Melodic minor' },
];

/** Circle-of-fifths default tonic display order (never a required sequence). */
export const CIRCLE_OF_FIFTHS_TONICS: Tonic[] = [
  'C',
  'G',
  'D',
  'A',
  'E',
  'B',
  'F#',
  'Db',
  'Ab',
  'Eb',
  'Bb',
  'F',
];
